import { useState } from 'react'
import type { ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import GameweekDetailHeading from '../GameweekDetailHeading'
import { CHIP_ICONS } from '../chipIcons'
import GameweekPicker from '../GameweekPicker'
import { buildGameweekPickerRows } from '../../gameweekPicker'
import RulesInfoButton from '../../features/rules/RulesInfoButton'
import DeadlineCountdownLine from './DeadlineCountdownLine'
import { useDeadlineCountdown } from './useDeadlineCountdown'
import type { GameweekChips } from './useGameweekChips'
import type { GwPointsHero } from './useGwPointsHero'
import type { Game, GameweekResult } from '../../types'

// JSX needs a capitalised binding, and both counters must use the same icons as everywhere
// else the chip appears, so alias them off the shared map rather than importing icons here.
const GoalfestIcon = CHIP_ICONS.goalfest
const CleanSweepIcon = CHIP_ICONS.cleanSweep

interface GameweekGridHeaderProps {
  gameweek: number
  games: Game[]
  history: GameweekResult[]
  /** The three chips' used-gameweek lists — the picker rows' chip badges come off these. */
  chipUsage: {
    doubleUpUsedGameweeks: number[]
    cleanSweepUsedGameweeks: number[]
    goalfestUsedGameweeks: number[]
  }
  chips: GameweekChips
  hero: GwPointsHero
  deadlinePassed: boolean
  /** `serverTime − Date.now()` from the last live poll — the countdown runs
   *  off the server's clock rather than this device's, so a skewed one still reaches zero when
   *  the server starts rejecting. 0 until the first poll lands. */
  serverOffsetMs: number
  noPredictionsYet: boolean
  allPredicted: boolean
  displayedPredictedCount: number
  allFixturesResolved: boolean
  goalfestLiveBonus: number
  cleanSweepClearedSoFar: boolean
  gameweekPointsSoFar: number
  onSelectHistoryGameweek: (
    gameweek: number,
    source?: { anchorEl: HTMLElement | null; clone: ReactNode }
  ) => void
  onHistoryPressFeedback?: () => void
  historyTransitionActive: boolean
  /** Open the gameweek's top scorer's profile, landing on their "GW N Calls" sheet. Absent when
   *  there is nobody to open — see `hero.topScorer`. */
  onOpenTopScorer?: (userId: string, displayName: string) => void
}

/** The grid's header block: the gameweek title/picker control, the deadline countdown,
 *  the prediction-progress dots, the GW Points hero row, and the live chip counters.
 */
function GameweekGridHeader({
  gameweek,
  games,
  history,
  chipUsage,
  chips,
  hero,
  deadlinePassed,
  serverOffsetMs,
  noPredictionsYet,
  allPredicted,
  displayedPredictedCount,
  allFixturesResolved,
  goalfestLiveBonus,
  cleanSweepClearedSoFar,
  gameweekPointsSoFar,
  onSelectHistoryGameweek,
  onHistoryPressFeedback,
  historyTransitionActive,
  onOpenTopScorer,
}: GameweekGridHeaderProps) {
  const countdown = useDeadlineCountdown(games, deadlinePassed, serverOffsetMs)
  const { activeChip, activeChipIcon, isGoalfestActive, isCleanSweepActive } = chips
  const {
    globalAverage,
    gameweekHighest,
    topScorer,
    heroTier,
    stampPlaying,
    goalfestPulsing,
    renderedPoints,
  } = hero

  // Gameweek picker. The title is the control — this screen is the root of the
  // predict tab, so the header's old back arrow had nothing to go back to and has been removed.
  // The open state lives on the grid, which stays mounted underneath the results modal, so
  // closing the modal returns to the screen with the dropdown still open.
  const [pickerOpen, setPickerOpen] = useState(false)
  const pickerRows = buildGameweekPickerRows({
    currentGameweek: gameweek,
    history,
    ...chipUsage,
  })

  const handleSelectPickerGameweek = (
    selected: number,
    sourceAnchorEl: HTMLElement | null
  ) => {
    const row = pickerRows.find((r) => r.gameweek === selected)
    const historyEntry = history.find((entry) => entry.gameweek === selected)
    onSelectHistoryGameweek(selected, {
      anchorEl: sourceAnchorEl,
      // The clone is the *destination's* markup — the modal heading this block becomes — so the
      // flight's final frame is a pixel match for the real heading.
      clone: (
        <GameweekDetailHeading
          gameweek={selected}
          totalPoints={historyEntry?.points ?? 0}
          activeChips={row?.chips ?? []}
        />
      ),
    })
  }

  // Every fixture called, deadline still open: the header has no progress row and no
  // points row, so the countdown is left alone above the chip row's own clearance.
  const noProgressRow = !deadlinePassed && !noPredictionsYet && allPredicted && games.length > 0

  return (
    <div
      className={`gameweek-grid__header${
        noProgressRow ? ' gameweek-grid__header--tight' : ''
      }`}
    >
      <div className="gameweek-grid__title-row">
        <button
          type="button"
          className="gameweek-grid__title-btn"
          onClick={() => setPickerOpen((open) => !open)}
          aria-expanded={pickerOpen}
          aria-controls="gameweek-picker"
        >
          <span className="gameweek-grid__title-text">Gameweek {gameweek}</span>
          <ChevronDown
            className={`gameweek-grid__title-chevron${
              pickerOpen ? ' gameweek-grid__title-chevron--open' : ''
            }`}
            aria-hidden="true"
          />
        </button>
        {pickerOpen && (
          <GameweekPicker
            rows={pickerRows}
            onSelectGameweek={handleSelectPickerGameweek}
            onPressFeedback={onHistoryPressFeedback}
            lifting={historyTransitionActive}
          />
        )}
      </div>
      {games.length === 0 ? (
        <p className="gameweek-grid__subtitle">No fixtures loaded yet</p>
      ) : (
        <>
          {countdown.showCountdown && countdown.label !== null && (
            <DeadlineCountdownLine
              label={countdown.label}
              isUrgent={countdown.isUrgent}
              info={<RulesInfoButton section="deadlines" />}
            />
          )}
          {!deadlinePassed && !noPredictionsYet && !allPredicted && (
            <div
              className="gameweek-grid__progress-dots"
              role="img"
              aria-label={`${displayedPredictedCount} of ${games.length} predicted`}
            >
              {Array.from({ length: games.length }, (_, index) => (
                <span
                  key={index}
                  className={`gameweek-grid__progress-dot ${
                    index < displayedPredictedCount ? 'gameweek-grid__progress-dot--filled' : ''
                  }`}
                />
              ))}
            </div>
          )}
        </>
      )}
      {(games.length === 0 || deadlinePassed) && (
        <div className="gameweek-grid__points-row">
          <div className="gameweek-grid__points-stat">
            <p className="gameweek-grid__points-stat-value">{globalAverage}</p>
            <p className="gameweek-grid__points-stat-label label-eyebrow">Average</p>
          </div>
          <div
            className={[
              'gameweek-grid__points-card',
              activeChip
                ? 'gameweek-grid__points-card--chip-active'
                : heroTier
                  ? `gameweek-grid__points-card--tier-${heroTier.name.toLowerCase()}`
                  : null,
              stampPlaying ? 'gameweek-grid__points-card--stamp' : null,
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <p className="gameweek-grid__points-eyebrow label-section">GW Points</p>
            <p className="gameweek-grid__points-value">{renderedPoints}</p>
            {activeChip ? (
              <div className="gameweek-grid__points-band">
                {activeChipIcon &&
                  (() => {
                    const ChipIcon = activeChipIcon
                    return (
                      <ChipIcon
                        className="gameweek-grid__points-band-icon"
                        aria-hidden="true"
                      />
                    )
                  })()}
                <span className="gameweek-grid__points-band-label label-eyebrow">
                  {activeChip.name.toUpperCase()}
                </span>
              </div>
            ) : null}
          </div>
          {/* Pressable only while there is somewhere to go: a named top scorer who isn't the
              viewer. Otherwise it stays the plain stat it has always been — a button that opens
              nothing is worse than text, and assistive tech would announce a control either way. */}
          {topScorer && onOpenTopScorer ? (
            <button
              type="button"
              className="gameweek-grid__points-stat gameweek-grid__points-stat--pressable"
              onClick={() => onOpenTopScorer(topScorer.userId, topScorer.displayName)}
              aria-label={`Highest, ${gameweekHighest} points by ${topScorer.displayName}. See their calls.`}
            >
              <span className="gameweek-grid__points-stat-value">{gameweekHighest}</span>
              <span className="gameweek-grid__points-stat-label label-eyebrow">Highest</span>
            </button>
          ) : (
            <div className="gameweek-grid__points-stat">
              <p className="gameweek-grid__points-stat-value">{gameweekHighest}</p>
              <p className="gameweek-grid__points-stat-label label-eyebrow">Highest</p>
            </div>
          )}
        </div>
      )}
      {deadlinePassed && isGoalfestActive && (
        <p
          className={`gameweek-grid__goalfest-counter${
            allFixturesResolved ? ' gameweek-grid__goalfest-counter--settled' : ''
          }${goalfestPulsing ? ' gameweek-grid__goalfest-counter--pulse' : ''}`}
        >
          <GoalfestIcon className="gameweek-grid__goalfest-counter-icon" aria-hidden="true" />
          Goalfest +{goalfestLiveBonus}
        </p>
      )}
      {deadlinePassed && isCleanSweepActive && (
        <p
          className={`gameweek-grid__cleansweep-counter${
            allFixturesResolved ? ' gameweek-grid__cleansweep-counter--settled' : ''
          }${!cleanSweepClearedSoFar ? ' gameweek-grid__cleansweep-counter--missed' : ''}`}
        >
          <CleanSweepIcon className="gameweek-grid__cleansweep-counter-icon" aria-hidden="true" />
          {cleanSweepClearedSoFar
            ? `On track for ${gameweekPointsSoFar}`
            : 'Missed — heading to 0'}
        </p>
      )}
    </div>
  )
}

export default GameweekGridHeader
